import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Mail, Plus, Pencil, Trash2, Landmark } from "lucide-react";

const empty = { titolo: "", oggetto: "", testo: "", prodotti: [], attivo: true, ordine: 99 };

export default function ModelliEmailTab() {
  const [data, setData] = useState({ modelli: [], impostazioni: {}, segnaposto: [] });
  const [prodotti, setProdotti] = useState([]);
  const [imp, setImp] = useState({ intestatario: "", iban: "" });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = useCallback(() => {
    api.get("/modelli-email").then(({ data: d }) => { setData(d); setImp(d.impostazioni); }).catch(() => {});
    api.get("/catalogo/prodotti").then(({ data: d }) => setProdotti(d)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const nomeProd = (c) => prodotti.find((p) => p.code === c)?.nome || c;
  const openEdit = (m) => { setEditing(m); setForm(m ? { ...m } : empty); setOpen(true); };

  const save = async () => {
    try {
      if (editing) await api.put(`/modelli-email/${editing.id}`, form); else await api.post("/modelli-email", form);
      toast.success("Modello salvato"); setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (m) => {
    if (!window.confirm(`Eliminare il modello "${m.titolo}"?`)) return;
    try { await api.delete(`/modelli-email/${m.id}`); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const saveImp = async () => {
    try { const { data: d } = await api.put("/modelli-email/impostazioni", imp); setImp(d); toast.success("Dati bonifico salvati"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const toggleProd = (c, on) => setForm((f) => ({ ...f, prodotti: on ? [...f.prodotti, c] : f.prodotti.filter((x) => x !== c) }));

  return (
    <div className="space-y-5" data-testid="modelli-email-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-heading text-lg text-slate-900">Modelli email</h3>
          <p className="text-sm text-slate-500">Testi pronti da scegliere in "Invia al cliente". Segnaposto: {data.segnaposto.map((s) => `{${s}}`).join(" ")}</p>
        </div>
        <Button onClick={() => openEdit(null)} className="brand-gradient text-white border-0" data-testid="modello-nuovo"><Plus className="h-4 w-4 mr-2" /> Nuovo modello</Button>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="modelli-impostazioni">
        <div className="flex items-center gap-2 mb-3"><Landmark className="h-4 w-4 text-indigo-600" /><h4 className="font-medium text-slate-800 text-sm">Dati per il bonifico (usati da {"{intestatario}"} e {"{iban}"})</h4></div>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 items-end">
          <div className="space-y-1.5"><Label>Intestatario</Label><Input value={imp.intestatario} onChange={(e) => setImp({ ...imp, intestatario: e.target.value })} data-testid="modelli-intestatario" /></div>
          <div className="space-y-1.5"><Label>IBAN</Label><Input value={imp.iban} onChange={(e) => setImp({ ...imp, iban: e.target.value })} data-testid="modelli-iban" /></div>
          <Button variant="outline" onClick={saveImp} data-testid="modelli-impostazioni-salva">Salva</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {data.modelli.map((m) => (
          <div key={m.id} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`modello-${m.id}`}>
            <div className="flex items-start justify-between gap-2 mb-1">
              <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-indigo-600" /><h4 className="font-medium text-slate-900">{m.titolo}</h4>{!m.attivo && <span className="text-[10px] text-slate-400 border rounded px-1">disattivo</span>}</div>
              <div className="flex gap-1">
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(m)} data-testid={`modello-modifica-${m.id}`}><Pencil className="h-4 w-4" /></Button>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => remove(m)} data-testid={`modello-elimina-${m.id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
            <p className="text-xs text-slate-500 mb-2">{m.prodotti.length ? m.prodotti.map(nomeProd).join(", ") : "Tutti i prodotti"}</p>
            <p className="text-xs text-slate-700 font-medium">{m.oggetto}</p>
            <p className="text-xs text-slate-500 whitespace-pre-line line-clamp-4 mt-1">{m.testo}</p>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Modifica modello" : "Nuovo modello"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Titolo</Label><Input value={form.titolo} onChange={(e) => setForm({ ...form, titolo: e.target.value })} data-testid="modello-titolo" /></div>
            <div className="space-y-1.5"><Label>Oggetto email</Label><Input value={form.oggetto} onChange={(e) => setForm({ ...form, oggetto: e.target.value })} data-testid="modello-oggetto" /></div>
            <div className="space-y-1.5"><Label>Testo</Label><Textarea rows={9} value={form.testo} onChange={(e) => setForm({ ...form, testo: e.target.value })} data-testid="modello-testo" /></div>
            <div className="space-y-1.5">
              <Label>Prodotti (nessuno selezionato = tutti)</Label>
              <div className="grid grid-cols-2 gap-1.5">
                {prodotti.map((p) => (
                  <label key={p.code} className="flex items-center gap-2 text-sm cursor-pointer"><Checkbox checked={form.prodotti.includes(p.code)} onCheckedChange={(v) => toggleProd(p.code, !!v)} data-testid={`modello-prod-${p.code}`} />{p.nome}</label>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-3"><Switch checked={!!form.attivo} onCheckedChange={(v) => setForm({ ...form, attivo: v })} data-testid="modello-attivo" /><Label className="text-sm">Attivo</Label></div>
          </div>
          <DialogFooter><Button onClick={save} disabled={!form.titolo || !form.testo} className="brand-gradient text-white border-0" data-testid="modello-salva">Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
